<script setup lang="ts">
import { useWindowFocus } from '@vueuse/core';

import { type AuctionItemEntity, AuctionState } from '~/apiClient';

// PROPS
interface Props {
    auction: AuctionItemEntity;
}

const { auction } = defineProps<Props>();

// COMPOSABLES
const { logError } = useLogs();
const { totalSeconds } = useCountdown(computed(() => auction.expiresAt));
const focused = useWindowFocus();

// DATA
const timeout = ref();
const audio = useTemplateRef<HTMLAudioElement>('audio');
const countdownAlert = computed(() => auction.state === AuctionState.Running && totalSeconds.value < 60 && totalSeconds.value > 0);
const loopTime = computed(() => (totalSeconds.value < 10 ? 500 : 1000));

// METHODS
const play = (): void => {
    timeout.value = setTimeout(() => {
        // I need to set the timeout on start to prevent start before focused is fully triggered
        if (!audio.value || !audio.value.paused || !focused.value) {
            return;
        }

        audio.value.currentTime = 0;
        audio.value
            .play()
            .catch(e => logError('Error starting audio track', { error: e }))
            .finally(() => (timeout.value = null));
    }, loopTime.value);
};

const pause = (): void => {
    if (!audio.value) {
        return;
    }

    audio.value.pause();
};

const clear = (): void => timeout.value && clearTimeout(timeout.value);

// HANDLERS
const onEnded = (): void => {
    if (timeout.value) {
        return;
    }

    play();
};

// WATCHERS
watch([focused, audio], ([f, a]) => (f && a ? play() : pause()));

// LIFECYCLE
onBeforeRouteLeave(clear);
onBeforeUnmount(clear);
</script>
<template>
    <audio v-if="countdownAlert" ref="audio" class="hidden" src="/sounds/countdown.mp3" autoplay @ended="onEnded" />
</template>
